import React, { useEffect } from "react";
import {
  CollaboratorDetails,
  CollaboratorHistoryProfessionalExperience,
  SubmitCollaboratorsHistoryProfessionalExperienceRequest,
} from "../../../../../../../types/api/collaborators.type";
import { Modal } from "../../../../../../../components/core/Modal/Modal";
import {
  Button,
  ButtonVariants,
} from "../../../../../../../components/core/Button/Button";
import {
  ABSTRACT_LABEL,
  ADD_EXPERIENCE_LABEL,
  CANCEL_LABEL,
  COMPANY_NAME_LABEL,
  CONFIRM_LABEL,
  DATA_ADDED_SUCCESS_LABEL,
  DATA_UPDATED_SUCCESS_LABEL,
  MUST_BE_AFTER_THAN_LABEL,
  EDIT_EXPERIENCE_LABEL,
  ENDING_DATE_LABEL,
  ERROR_LABEL,
  ERROR_MESSAGE_LABEL,
  EXPERIENCE_OVERLAP_ERROR_MESSAGE,
  EXPERIENCE_START_DATE_ERROR_MESSAGE,
  INVALID_DATE_LABEL,
  IS_NOT_VALID_LABEL,
  IS_REQUIRED_LABEL,
  STARTING_DATE_LABEL,
} from "../../../../../../../constants/Dictionary";
import { ReactComponent as CloseIcon } from "../../../../../../../icons/close.svg";
import { ReactComponent as CheckIcon } from "../../../../../../../icons/check.svg";
import { Loader } from "../../../../../../../components/core/Loader/Loader";
import {
  CalendarInput,
  CalendarMode,
} from "../../../../../../../components/core/CalendarInput/CalendarInput";
import TextInput from "../../../../../../../components/core/TextInput/TextInput";
import dayjs from "dayjs";
import * as yup from "yup";
import { Controller, Resolver, SubmitHandler, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { useNotification } from "../../../../../../../hooks/notification.hook";
import {
  EN_DATE_FORMAT,
  isMonthSameOrAfter,
} from "../../../../../../../utils/date/date.utils";
import { FormField } from "../../../../../../../components/core/FormField/FormField";
import { StyledHistoryExperienceModal } from "./PastExperience.styled";
import ToggleSwitch from "../../../../../../../components/core/ToggleSwitch/ToggleSwitch";
import { FontWeightEnum } from "../../../../../../../types/AppTypes";
import { BodyTyp } from "../../../../../../../components/Typography/Typography";
import { SingleErrorResponse } from "../../../../../../../types/api/generic.type";
import { AxiosError } from "axios";
import {
  CustomerErrorCodesEnum,
  StatusCodes,
} from "../../../../../../../constants/AppConstants";
import { useSubmitHistoryExperience } from "../../../../../../../hooks/professionalPortrait.hook";

type PastExperienceModalProps = {
  record?: CollaboratorHistoryProfessionalExperience | null;
  isOpen: boolean;
  onClose: () => void;
  onSave: () => void;
  collaborator: CollaboratorDetails;
};

export type FormData = {
  companyName: string;
  startDate: Date | null;
  endDate?: Date | null;
  isAbstract: boolean;
};

export const PastExperienceModal = ({
  record: experience,
  isOpen,
  onClose,
  onSave,
  collaborator,
}: PastExperienceModalProps) => {
  const { notification } = useNotification();

  const profileId = collaborator.id;

  const {
    mutateAsync: submitHistoryExperience,
    isPending: isSubmittingHistoryExperience,
  } = useSubmitHistoryExperience(profileId);

  const schema = yup
    .object({
      companyName: yup
        .string()
        .required(`${COMPANY_NAME_LABEL} ${IS_REQUIRED_LABEL}`),
      startDate: yup
        .date()
        .nullable()
        .typeError(`${INVALID_DATE_LABEL}`)
        .max(new Date(), `${STARTING_DATE_LABEL} ${IS_NOT_VALID_LABEL}`)
        .required(`${STARTING_DATE_LABEL} ${IS_REQUIRED_LABEL}`),
      endDate: yup
        .date()
        .nullable()
        .typeError(`${INVALID_DATE_LABEL}`)
        .max(new Date(), `${ENDING_DATE_LABEL} ${IS_NOT_VALID_LABEL}`)
        .test(
          "is-after-startDate",
          `La ${ENDING_DATE_LABEL} ${MUST_BE_AFTER_THAN_LABEL} ${STARTING_DATE_LABEL}.`,
          function (endDate) {
            const { startDate } = this.parent;
            if (!endDate || !startDate) {
              return true;
            }
            return isMonthSameOrAfter(endDate, startDate);
          },
        )
        .when(["isAbstract"], {
          is: (isAbstract: boolean) => isAbstract,
          then: (field) =>
            field.required(`${ENDING_DATE_LABEL} ${IS_REQUIRED_LABEL}`),
          otherwise: (field) => field.optional(),
        }),
      isAbstract: yup.boolean().default(false),
    })
    .required();

  const {
    watch,
    setValue,
    handleSubmit,
    reset,
    control,
    formState: { errors, isDirty },
    setError,
  } = useForm<FormData>({
    resolver: yupResolver(schema) as Resolver<FormData>,
  });

  useEffect(() => {
    if (experience) {
      reset({
        companyName: experience.companyName,
        startDate: dayjs(experience.startDate).toDate(),
        endDate: experience.endDate
          ? dayjs(experience.endDate).toDate()
          : undefined,
        isAbstract: experience.isAbstract,
      });
    } else {
      reset({
        companyName: "",
        startDate: null,
        endDate: null,
        isAbstract: false,
      });
    }
  }, [experience, isOpen]);

  const onSubmit: SubmitHandler<FormData> = async (data: FormData) => {
    try {
      const body: SubmitCollaboratorsHistoryProfessionalExperienceRequest = {
        isAbstract: data.isAbstract,
        companyName: data.companyName,
        startDate: dayjs(data.startDate).format(EN_DATE_FORMAT),
        endDate: data.endDate
          ? dayjs(data.endDate).format(EN_DATE_FORMAT)
          : null,
      };
      await submitHistoryExperience({ body, experience });
      onSave();
      closeModal();
      experience
        ? notification.success(DATA_UPDATED_SUCCESS_LABEL, undefined, "toast")
        : notification.success(DATA_ADDED_SUCCESS_LABEL, undefined, "toast");
    } catch (error) {
      console.error(error);
      const errorObj = error as AxiosError<SingleErrorResponse>;
      if (
        errorObj.response &&
        errorObj.response.status === StatusCodes.CUSTOM_ERROR
      ) {
        if (
          errorObj.response.data.code ===
          CustomerErrorCodesEnum.ABSTRACT_EXPERIENCE_OVERLAP
        ) {
          setError("startDate", {
            message: EXPERIENCE_OVERLAP_ERROR_MESSAGE,
          });
          setError("endDate", {
            message: EXPERIENCE_OVERLAP_ERROR_MESSAGE,
          });
        } else if (
          errorObj.response.data.code ===
          CustomerErrorCodesEnum.RELATED_PROJECTS_TIMELINE_OUT_OF_EXPERIENCE_RANGE
        ) {
          setError("startDate", {
            message: EXPERIENCE_START_DATE_ERROR_MESSAGE,
          });
          setError("endDate", {
            message: EXPERIENCE_START_DATE_ERROR_MESSAGE,
          });
        } else {
          notification.error(ERROR_LABEL, ERROR_MESSAGE_LABEL);
        }
      } else {
        notification.error(ERROR_LABEL, ERROR_MESSAGE_LABEL);
      }
    }
  };

  const closeModal = () => {
    onClose();
  };

  const setCompanyName = (companyName: string) => {
    setValue("companyName", companyName, {
      shouldDirty: true,
      shouldValidate: true,
    });
  };

  const isAbstract = watch("isAbstract");
  const startDate = watch("startDate");

  return (
    <Modal
      fullSize={false}
      label={experience ? EDIT_EXPERIENCE_LABEL : ADD_EXPERIENCE_LABEL}
      isOpen={isOpen}
      leftButton={
        <Button
          label={CANCEL_LABEL}
          variant={ButtonVariants.SECONDARY}
          icon={<CloseIcon />}
          onClick={closeModal}
          disabled={isSubmittingHistoryExperience}
        />
      }
      rightButton={
        <Button
          label={CONFIRM_LABEL}
          variant={ButtonVariants.PRIMARY}
          icon={<CheckIcon />}
          disabled={isSubmittingHistoryExperience || !isDirty}
          type="submit"
          form="historyExperienceForm"
        />
      }
    >
      <StyledHistoryExperienceModal>
        <Loader loading={isSubmittingHistoryExperience}>
          <form id="historyExperienceForm" onSubmit={handleSubmit(onSubmit)}>
            <div className="input-row">
              <FormField error={errors.isAbstract?.message}>
                <Controller
                  name="isAbstract"
                  control={control}
                  render={({ field: { onChange, ...field } }) => {
                    return (
                      <div className="toggle-container">
                        <ToggleSwitch
                          onChange={(checked) => {
                            onChange(checked);
                            if (checked) {
                              setCompanyName("ABSTRACT S.R.L.");
                            } else if (experience) {
                              setCompanyName(experience.companyName);
                            } else {
                              setCompanyName("");
                            }
                          }}
                          {...field}
                        />
                        <BodyTyp variant="2" fontWeight={FontWeightEnum.MEDIUM}>
                          {ABSTRACT_LABEL}
                        </BodyTyp>
                      </div>
                    );
                  }}
                />
              </FormField>
              <FormField error={errors.companyName?.message}>
                <Controller
                  name="companyName"
                  control={control}
                  render={({ field }) => (
                    <TextInput
                      label={COMPANY_NAME_LABEL}
                      maxLength={255}
                      readOnly={isAbstract}
                      allowClear={!isAbstract}
                      {...field}
                    />
                  )}
                />
              </FormField>
            </div>
            <div className="input-row">
              <FormField error={errors.startDate?.message}>
                <Controller
                  name="startDate"
                  control={control}
                  render={({ field }) => (
                    <CalendarInput
                      label={STARTING_DATE_LABEL}
                      pickerMode={CalendarMode.MONTHS}
                      maxDate={new Date()}
                      error={errors.startDate?.message}
                      {...field}
                    />
                  )}
                />
              </FormField>
              <FormField error={errors.endDate?.message}>
                <Controller
                  name="endDate"
                  control={control}
                  render={({ field }) => (
                    <CalendarInput
                      label={ENDING_DATE_LABEL}
                      pickerMode={CalendarMode.MONTHS}
                      minDate={
                        startDate && !isNaN(startDate.getTime())
                          ? startDate
                          : undefined
                      }
                      maxDate={new Date()}
                      error={errors.endDate?.message}
                      {...field}
                    />
                  )}
                />
              </FormField>
            </div>
          </form>
        </Loader>
      </StyledHistoryExperienceModal>
    </Modal>
  );
};
